import { Alert, Button, Spinner } from 'flowbite-react';
import { Trans, useTranslation } from 'react-i18next';
import { HiInformationCircle } from 'react-icons/hi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConfigContext } from '../../lib/context/AppConfig';
import { useFirebaseContext } from '../../lib/context/Firebase';
import { AuthenticationResult } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../lib/hooks.ts';
import { login } from '../../lib/model/user/userSlice.ts';

// Landing page for `inviteRedirectUrl` (specs/011-entra-external-users/plan.md's "Design" section
// — `EntraExternalUsersService` sends Microsoft Graph `<app>/external-login` as the redirect target
// for its `POST /invitations` calls). By the time the browser reaches this page, Microsoft's own
// hosted redemption UI has already run in full (sign-in with an existing Microsoft account, or
// Email one-time-passcode for a guest with none — research.md Decision 2) and the B2B guest object
// already exists in this tenant's directory. This page's only remaining job is a completely
// ordinary MSAL sign-in, identical in shape to Login.tsx, so the guest's browser ends up holding an
// access token whose `roles` claim carries `GUEST` (plan.md "Why nothing in the auth layer
// changes" — same `iss`/`tid`/`aud`, so the existing `login` thunk / token-exchange endpoint needs
// no guest-specific branch). Kept as its own component (rather than reusing Login.tsx directly) so
// the copy reads as "completing your invitation" instead of a generic sign-in prompt, and so its
// post-login destination can differ from Login.tsx's.
export default function ExternalLogin() {
    const {t} = useTranslation("external-login");
    const appConfig = useConfigContext();
    const firebase = useFirebaseContext();
    const dispatch = useAppDispatch();
    const {instance, accounts} = useMsal();
    const [error, setError] = useState<string | undefined>(undefined)
    const [loggingIn, setLoggingIn] = useState<boolean>(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Same scope as Login.tsx (contracts/app-config.json.md) — the single exposed API scope.
    const request = useMemo(() => ({scopes: [appConfig.msalConfig.scope]}), [appConfig.msalConfig.scope]);

    const completeLogin = useCallback((result: AuthenticationResult) => {
        // Unlike Login.tsx, a redeemed guest has no natural "page they were trying to reach" — they
        // arrived here straight from Microsoft's hosted redemption page, not from ProtectedRoute
        // bouncing them off some deep link. Default to /overview, matching the destination the
        // retired Firebase email-link flow used to navigate a freshly-signed-in guest to (rather
        // than "/", which Home.tsx would otherwise redirect an employee-shaped session on to
        // /signup). `next` is still honoured if present, for parity with Login.tsx.
        const next = searchParams.get('next') ?? '/overview';

        // Needed for prepareHeaders (emptyApi.ts) to know which account to silently reacquire
        // a token for on subsequent API calls — MSAL doesn't set this automatically.
        instance.setActiveAccount(result.account);

        console.log("Microsoft access token obtained, exchanging it for a Firebase identity");

        dispatch(login({accessToken: result.accessToken, auth: firebase.auth, account: result.account}))
            .unwrap()
            .then(() => {
                setLoggingIn(false);
                console.log('Navigating to next page ' + next);
                navigate(next)
            })
            .catch((error: unknown) => {
                setLoggingIn(false);
                setError(error instanceof Error ? error.message : String(error));
            })
    }, [dispatch, firebase.auth, instance, navigate, searchParams]);

    // Popup sign-in must be triggered by a direct user gesture (browser popup blockers kill
    // auto-triggered popups) — the redirect flow has no such restriction and can auto-trigger.
    const signIn = useCallback(() => {
        setLoggingIn(true);

        const action = appConfig.usePopup
            ? instance.loginPopup(request)
            : instance.loginRedirect(request);

        action
            .then((result) => {
                // Redirect flow navigates the browser away; only the popup flow resolves here.
                if (result) {
                    completeLogin(result);
                }
            })
            .catch((error: unknown) => {
                setLoggingIn(false);
                setError(error instanceof Error ? error.message : String(error));
            })
    }, [instance, request, appConfig.usePopup, completeLogin]);

    // Reacquires using the cached MSAL account (if any) without an interactive prompt, falling
    // back to signIn() (loginPopup/loginRedirect) only if that fails (e.g. the refresh token
    // itself expired or was revoked). Shared by the mount effect below and retry(): both need the
    // same "prefer silent, fall back to interactive" behavior rather than always going interactive.
    const reacquireSilentlyOrSignIn = useCallback(() => {
        if (accounts.length > 0) {
            setLoggingIn(true);
            instance.acquireTokenSilent({...request, account: accounts[0]})
                .then((result) => completeLogin(result))
                .catch((error: unknown) => {
                    console.error("acquireTokenSilent failed; falling back to interactive sign-in", error);
                    signIn();
                });
        } else {
            signIn();
        }
    }, [accounts, instance, request, completeLogin, signIn]);

    useEffect(() => {
        instance.handleRedirectPromise()
            .then((result) => {
                if (result) {
                    completeLogin(result);
                } else if (accounts.length > 0) {
                    // Landed here with a still-cached MSAL account but no redirect response —
                    // e.g. reopening the tab after a while. See Login.tsx's identical handling.
                    reacquireSilentlyOrSignIn();
                } else if (!appConfig.usePopup) {
                    signIn();
                }
            })
            .catch((error: unknown) => {
                setError(error instanceof Error ? error.message : String(error));
            })
        // Only run once on mount — signIn/completeLogin/request are recreated per render but the
        // redirect response only needs handling once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const retry = useCallback(() => {
        setError(undefined);
        // Not signIn() directly: a failed token exchange (the common retry case) still has a
        // perfectly good cached MSAL account — retrying interactively would pop a real Microsoft
        // sign-in prompt for no reason. reacquireSilentlyOrSignIn only goes interactive if the
        // silent reacquire itself fails.
        reacquireSilentlyOrSignIn();
    }, [reacquireSilentlyOrSignIn]);

    if (error !== undefined) {
        return (
            <div>
                <Alert color="failure" icon={HiInformationCircle}>
                    {t('failed_login')}: {error}
                </Alert>
                <Button color="primary" onClick={retry} className="mt-4">
                    {t('try_again')}
                </Button>
            </div>
        )
    }

    if (loggingIn || !appConfig.usePopup) {
        return (
            <div>
                <Button disabled color="primary">
                    <Spinner>
                        <Trans t={t}>loading</Trans>
                    </Spinner>
                </Button>
            </div>
        )
    }

    return (
        <div>
            <Button color="primary" onClick={signIn}>
                {t('sign_in_now')}
            </Button>
        </div>
    )
}
